import { useCallback, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { HeaderData, HeaderDataContext } from '@raisin/cp-layout';
import { DEFAULT_LOCALE, getDistributor, getLocale } from '@raisin/cp-utils';
import { createAsyncAction, METHODS } from 'api';
import { MICROSERVICES_NAMES } from 'api/types';
import { getAuthorizationToken } from 'api/utils';
import { productTypes } from 'types';
import { getDistributionChannelId } from 'utils/getDistributionChannel';
import { matchAccessToProducts } from 'utils/productAccessMap';
import { useBacNumber } from './useBacNumber';

export const useProductsAccess = () => {
  const router = useRouter();
  const locale = getLocale(router.locale || DEFAULT_LOCALE)?.intlFormat;
  const { customerData } = useContext(HeaderDataContext) as HeaderData;
  const [productsAccess, setProductsAccess] = useState<productTypes[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const bacNumber = useBacNumber();
  const { distributionChannelId, distributorId } = customerData || {};

  const fetchProductAccess = useCallback(async () => {
    const params = {
      customer_id: bacNumber,
      distributor_id: distributorId || getDistributor(),
      distribution_channel_id: distributionChannelId || getDistributionChannelId(locale),
    };
    try {
      setIsLoading(true);
      const response = await createAsyncAction(
        locale,
        MICROSERVICES_NAMES.DBS,
        'dbs.productsAccess',
        METHODS.GET,
        {
          params,
          headers: { authorization: getAuthorizationToken() },
        },
      );

      const formattedProductAccess = matchAccessToProducts(response);
      setProductsAccess(formattedProductAccess);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [bacNumber, distributionChannelId, distributorId, locale]);

  useEffect(() => {
    if (!distributorId) {
      return;
    }

    fetchProductAccess();
  }, [fetchProductAccess]);

  return { productsAccess, distributorId, isLoading, error };
};
